import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { Field, inputClass } from '../../../../../components/ui/Field';
import { Switch } from '../../../../../components/ui/Switch';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { createSupply, updateSupply } from '../../../../../features/supplies/suppliesThunks';
import {
  BOX_FIELDS,
  isContainerKind,
  SUPPLY_KIND_OPTIONS,
  buildSupplyPayload,
  supplyFormFrom,
  supplyProblem,
} from '../../../../../features/supplies/suppliesUtils';

/**
 * Creates a supply in a warehouse, or edits its card.
 *
 * The card only: balance and average cost are not here on purpose. They move
 * with a replenishment or an adjustment, which leave a trail.
 *
 * A container (box, envelope, bag) carries its dimensions and its tare: that
 * is what fills a parcel's logistic fields when it is used in one. Any other
 * kind has no dimensions to speak of, so the fields are not drawn.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object|null} props.warehouse - Where a new supply is created.
 * @param {object|null} props.supply - The supply being edited; `null` for a new one.
 * @param {(message: string) => void} props.onSaved - Told what happened, in a sentence.
 */
export const SupplyFormModal = ({ isOpen, onClose, warehouse, supply, onSaved }) => {
  const dispatch = useDispatch();
  const saving = useSelector((state) => state.supplies.saving);

  const [form, setForm] = useState(() => supplyFormFrom(supply));
  const [saveError, setSaveError] = useState(null);

  // Seeded on each opening.
  useEffect(() => {
    if (!isOpen) return;
    setForm(supplyFormFrom(supply));
    setSaveError(null);
  }, [isOpen, supply]);

  const isEditing = Boolean(supply);
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const problem = supplyProblem(form);

  const handleSave = async () => {
    setSaveError(null);
    const values = buildSupplyPayload(form);

    const action = await dispatch(
      isEditing
        ? updateSupply({ supplyId: supply.id, values })
        : createSupply({ warehouseId: warehouse.id, values })
    );

    if (action.meta.requestStatus !== 'fulfilled') {
      setSaveError(action.payload);
      return;
    }

    onSaved(
      isEditing
        ? 'Insumo actualizado.'
        : `Se creó ${values.sku}. Repón su stock para que empiece a costear.`
    );
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? `Editar ${supply.sku}` : 'Nuevo insumo'}
      testId="supply-form-modal"
    >
      <p className="text-sm text-text-secondary text-center -mt-4 mb-6">
        {isEditing ? supply.warehouse_name : warehouse?.name} · es de la bodega, compartido entre
        sus clientes
      </p>

      <div className="space-y-4">
        {saveError && (
          <div
            data-testid="supply-form-error"
            className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm"
          >
            {saveError}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="SKU" htmlFor="supply-sku">
            <input
              id="supply-sku"
              type="text"
              data-testid="supply-sku"
              value={form.sku}
              onChange={(event) => set({ sku: event.target.value })}
              disabled={saving}
              className={inputClass}
            />
          </Field>
          <Field label="EAN / código (opcional)" htmlFor="supply-barcode" hint="Para escanear al empacar.">
            <input
              id="supply-barcode"
              type="text"
              data-testid="supply-barcode"
              value={form.barcode}
              onChange={(event) => set({ barcode: event.target.value })}
              disabled={saving}
              className={inputClass}
            />
          </Field>
        </div>

        <Field label="Nombre" htmlFor="supply-name">
          <input
            id="supply-name"
            type="text"
            data-testid="supply-name"
            value={form.name}
            onChange={(event) => set({ name: event.target.value })}
            disabled={saving}
            className={inputClass}
          />
        </Field>

        <FilterableSelect
          variant="form"
          testId="supply-kind"
          label="Tipo"
          value={form.kind}
          onChange={(kind) => set({ kind })}
          options={SUPPLY_KIND_OPTIONS}
          placeholder="Elegir..."
          searchPlaceholder="Buscar..."
          disabled={saving}
        />

        {isContainerKind(form.kind) && (
          <div data-testid="supply-box-fields" className="space-y-2">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {BOX_FIELDS.map((field) => (
                <Field key={field.key} label={`${field.label} (${field.unit})`} htmlFor={`supply-${field.key}`}>
                  <input
                    id={`supply-${field.key}`}
                    type="number"
                    min="0"
                    step="1"
                    inputMode="numeric"
                    data-testid={`supply-${field.key}`}
                    value={form[field.key]}
                    onChange={(event) => set({ [field.key]: event.target.value })}
                    disabled={saving}
                    className={inputClass}
                  />
                </Field>
              ))}
            </div>
            <p className="text-xs text-text-muted">
              Lo que mide y lo que pesa vacío. Al usarlo en un bulto rellenan sus medidas y su
              peso de partida; el operario puede corregirlos.
            </p>
          </div>
        )}

        <Field
          label="Precio por unidad (por defecto)"
          htmlFor="supply-price"
          hint="Lo que se le cobra al cliente. Vacío significa que no se cobra."
        >
          <input
            id="supply-price"
            type="number"
            min="0"
            step="any"
            data-testid="supply-price"
            value={form.default_price}
            onChange={(event) => set({ default_price: event.target.value })}
            disabled={saving}
            className={inputClass}
          />
        </Field>

        <Switch
          checked={form.is_active}
          onChange={(is_active) => set({ is_active })}
          label="Activo"
          ariaLabel="Insumo activo"
          disabled={saving}
          testId="supply-active"
        />

        <p className="text-xs text-text-muted">
          El saldo y el costo promedio no se editan acá: se mueven con una reposición o un ajuste,
          que dejan rastro.
        </p>
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          loading={saving}
          loadingLabel="Guardando..."
          disabled={Boolean(problem)}
          onClick={handleSave}
          data-testid="supply-save"
        >
          {problem || 'Guardar'}
        </Button>
      </div>
    </Modal>
  );
};
